import Swiper from "swiper";

const SLIDE_TRANSITION_MS = 150;

const DEFAULT_SWIPER_CONFIG = {
    spaceBetween: window.tg.is_mobile_device ? 16 : 24,
    centeredSlides: false,
    slidesPerView: "auto",
    slidesOffsetBefore: 0,
    slidesOffsetAfter: 0,
    grabCursor: false,
    watchSlidesProgress: true,
    passiveListeners: true,
    shortSwipes: window.tg.is_mobile_device,
};

/*
 * Creates an initialization function object that
 * wraps all the required elements to initialize the
 * themed destinations swiper component.
 *
 * `rootElement` is the parent DOM element for the component and must have the
 * ID `themed-destinations`. It must also contain child elements with IDs
 * `theme-destinations-tabs` and `theme-destinations-body`.
 *
 * `headerElement` and `bodyElement` are both DOM elements for which the
 * actual Swiper instance will be initialized and attached, and must have the
 * IDs `theme-destinations-tabs` and `theme-destinations-body`, respectively.
 *
 * Each call to the returned function object will attempt to link the input
 * argument, `element` to the correct element reference, and once
 * `rootElement`, `headerElement`, and `bodyElement` have valid references,
 * then the final call to the function object will proceed with initializing
 * Swiper.
 *
 * @return {(HTMLElement) => void} A function object that, when invoked, will
 * initialize the themed destinations component once references to all the
 * required elements have been linked.
 *
 * */
export function createThemedDestinationsLazyInitializer() {
    let rootElement = null;
    let headerElement = null;
    let bodyElement = null;

    function setAndCheckAllElementsReady(element) {
        if (!rootElement && element.parentElement.id === "themed-destinations") {
            rootElement = element.parentElement;
        }

        if (!headerElement && element.id === "theme-destinations-tabs") {
            headerElement = element;
        }

        if (!bodyElement && element.id === "theme-destinations-body") {
            bodyElement = element;
        }

        return rootElement && headerElement && bodyElement;
    }

    return (element) => {
        if (!setAndCheckAllElementsReady(element)) return;

        // Configure & create swipers first, but don't initialize.
        //
        // The configs for both of these defer Swiper's internal initialization
        // until a call to `swiper.init()` is made.
        //
        // Calls to `new Swiper(...)` will inject a `swiper` a property into its
        // respective DOM element.
        //
        // This defer is necessary so that both DOM elements contain a reference
        // to its own swiper instance before the initialization callback is
        // executed. The initialization for both depend on each other containing
        // its own reference.
        const tabSwiperConfig = { init: false, ...createTabSwiperConfig(rootElement) };
        const bodySwiperConfig = { init: false, ...createBodySwiperConfig(rootElement) };

        const swipers = [
            new Swiper(`#${headerElement.id}.swiper-container`, tabSwiperConfig),
            new Swiper(`#${bodyElement.id}.swiper-container`, bodySwiperConfig),
        ];

        // Finally, initialize swipers
        for (const swiper of swipers) {
            swiper.init();
            swiper.initialized = true;
        }
    }
}

/*
 * Create configuration object for the themed destinations
 * tab bar Swiper instance.
 *
 * @param {HTMLElement} rootElement The root DOM element that contains the destinations tab bar and body
 *
 * @return {object} Object to be used for configuring Swiper for the tab bar component
 *
 * */
function createTabSwiperConfig(rootElement) {
    return {
        ...DEFAULT_SWIPER_CONFIG,
        loop: false,
        navigation: false,
        on: {
            init: function () {
                // Insert references to root element & body swiper into our swiper instance
                this.rootElement = rootElement;
                this.bodySwiper = this.rootElement.querySelector("#theme-destinations-body")?.swiper; // This is injected on `new Swiper(...)`

                // Add custom click event handlers to tab headings
                const tabContents = this.rootElement.querySelectorAll("[id^=tab-]");
                tabContents.forEach((element) => {
                    element.addEventListener("click", (event) => {
                        tabContents.forEach(element => element.firstElementChild.classList.remove("activeThemeTab"));
                        if (!this.initialized) {
                            return;
                        }

                        if (!this.bodySwiper) {
                            throw new Error(`couldn't obtain handle to body swiper instance`);
                        }

                        const dataElement = event.target.closest("[data-index]");
                        const dataIndex = dataElement?.getAttribute("data-index");

                        if (!dataIndex) {
                            throw new Error("could not find element with `data-index` attribute");
                        }

                        // Swipe body to current tab
                        dataElement.firstElementChild.classList.add("activeThemeTab");
                        const toIndex = parseInt(dataIndex);

                        // Manual translation to always snap selected tab to the left on swipe
                        this.realIndex = toIndex;
                        this.translateTo(
                            computeTabTranslation(this, toIndex),
                            SLIDE_TRANSITION_MS,
                            false, // Don't run event callbacks
                            false // Allow overflowing container boundaries
                        );

                        this.bodySwiper.slideTo(
                            computeBodyThemeIndex(toIndex, this.bodySwiper.params.loop, this.bodySwiper.loopedSlides),
                            SLIDE_TRANSITION_MS,
                            false // Don't run event callbacks
                        );

                        updateThemeImage(this.rootElement, toIndex);
                    });
                });

                // Update state and set initial tab position
                this.update();
                this.translateTo(
                    computeTabTranslation(this, 0),
                    SLIDE_TRANSITION_MS,
                    false, // Don't run event callbacks
                    false // Allow overflowing container boundaries
                );
            },

            resize: function () {
                this.translateTo(
                    computeTabTranslation(this, this.realIndex),
                    0, // We want this to instantly snap into the correct place
                    false, // Don't run event callbacks
                    false // Allow overflowing container boundaries
                );
            }
        },
    };
}

/*
 * Create configuration object for the themed destinations
 * body Swiper instance.
 *
 * @param {HTMLElement} rootElement The root DOM element that contains the destinations tab bar and body
 *
 * @return {object} Object to be used for configuring Swiper for the body component
 *
 * */
function createBodySwiperConfig(rootElement) {
    // Transition handler for swipes
    function onTransition() {
        if (!this.initialized) {
            return;
        }

        if (!this.tabSwiper) {
            throw new Error("couldn't obtain handle to tab headings swiper instance");
        }

        // Update tab selection on swipe
        const tabContents = this.rootElement.querySelectorAll("[id^=tab-]");

        // Manual translation to always snap selected tab to the left on swipe
        this.tabSwiper.realIndex = this.realIndex;
        this.tabSwiper.translateTo(
            computeTabTranslation(this.tabSwiper, this.realIndex),
            SLIDE_TRANSITION_MS,
            false, // Don't run event callbacks
            false // Allow overflowing container boundaries
        );

        // Update selected theme styling
        tabContents.forEach((element) => {
            element.firstElementChild.classList.remove("activeThemeTab");
            const dataIndex = element.getAttribute("data-index");
            if (dataIndex && dataIndex == this.realIndex) {
                element.firstElementChild.classList.add("activeThemeTab");
            }
        });

        updateThemeImage(this.rootElement, this.realIndex);
    }

    // Config for body content
    return {
        ...DEFAULT_SWIPER_CONFIG,
        loop: window.tg.is_mobile_device,
        resizeObserver: true,
        shortSwipes: true,
        navigation: {
            nextEl: ".btn-next-theme-destinations-body",
            prevEl: ".btn-prev-theme-destinations-body",
        },
        on: {
            init: function () {
                this.rootElement = rootElement;
                this.tabSwiper = this.rootElement.querySelector("#theme-destinations-tabs")?.swiper; // This is injected on `new Swiper(...)`
                this.update();
            },

            slideResetTransitionStart: onTransition,
            slideChangeTransitionStart: onTransition,
        },
    };
}

/*
 * Calculates the correct index for the specified theme index
 * `localIndex` based on whether `isLoopSwiper` is looped or not.
 *
 * For non-looped swipers, simply return `localIndex`.
 *
 * For looped swipers, add `totalLoopedSlides` to `localIndex`
 * to access the "current buffer" of slides, or the slide specified
 * by "global index":
 *
 *               Prev Buffer | Current Buffer | Next Buffer
 *               ---------------------------------------------
 *  local index: 0, 1, 2, 3  | 0, 1, 2, 3     | 0, 1, 2,  3
 * global index: 0, 1, 2, 3  | 4, 5, 6, 7     | 8, 9, 10, 11
 *                             ^
 *                             |
 *                              - We need to get the slide in this buffer.
 *
 * The "current buffer" is the list of slides currently being used in the view.
 * "Next" and "previous" buffers are the lists used when the view loops forwards
 * or backwards, respectively.
 *
 * @param {number} localIndex The index of the desired theme.
 * @param {boolean} isLoopSwiper `true` if `totalLoopedSlides` should be accounted for in index calculation.
 * @param {number} totalLoopedSlides The total amount of slides in the swiper instance that is being looped. Usually the same as total amount of themes.
 *
 * @return {number} The index to the correct Swiper theme
 *
 * */
function computeBodyThemeIndex(localIndex, isLoopSwiper = false, totalLoopedSlides = 0) {
    return isLoopSwiper
        ? localIndex + totalLoopedSlides
        : localIndex;
}

/*
 * Calculate the translation offset needed for a given `tabIndex` to be snapped
 * to the beginning of the tab header, or left-most position, taking into
 * account margins, gaps, etc. The offset will almost always be a negative
 * value.
 *
 *                             `tabIndex`
 *                                 |
 *    |-------Returned Value-------|
 *    v                            v
 *    ------------------------------------------------------------------------
 * <- Theme 1 ...| Longer Theme ...| Even Longer Theme | Even x N Longer Theme ->
 *    ------------------------------------------------------------------------
 *    ^     ^ ^  ^ ^          ^ ^  ^
 *    |-----| |--| |----------| |--|
 *     width + mr +   width    + mr
 *
 * @param {Swiper} swiper The swiper instance of the tab bar
 * @param {number} tabIndex The index of the theme for which we want the translation offset
 *
 * @return {number} The total amount of offset in pixels
 *
 * */
function computeTabTranslation(swiper, tabIndex) {
    const paddingLeft = 10;

    if (tabIndex < 1) return paddingLeft;

    const wrapperGap = parseInt(getComputedStyle(swiper.wrapperEl).columnGap) || 0;
    const slidesGap = swiper.params.spaceBetween ?? 0;

    return swiper.slidesSizesGrid
        .slice(0, tabIndex)
        .reduce((acc, size) => {
            return acc - (size + slidesGap + wrapperGap);
        }, wrapperGap + paddingLeft);
}

/*
 * Apply styling rules to the theme image for the specified `activeIndex`,
 * and remove styling rules for all other images.
 *
 * Theme image elements will be queried using `rootElement`
 * as the root element and NOT the entire page document.
 *
 * Theme image elements are expected to contain the `data-index`
 * attribute that corresponds to the current theme's index,
 * specified by `activeIndex`
 *
 * @param {HTMLElement} rootElement The root DOM element that contains the destinations tab bar and body
 * @param {number} activeIndex The index of the theme for which the image must be activated
 *
 * */
function updateThemeImage(rootElement, activeIndex) {
    rootElement.querySelectorAll("[id^=theme-image-]")
        .forEach((element) => {
            const dataIndex = element.getAttribute("data-index");
            if (!dataIndex) return;
            element.classList.toggle("theme-image-active", dataIndex == activeIndex);
            element.classList.toggle("theme-image-inactive", dataIndex != activeIndex);
        });
};
